import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';

const useNotifications = (options = {}) => {
    const { pollMs = 30000, enabled = true, onNewNotification } = options;
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const seenIdsRef = useRef(new Set());

    const fetchNotifications = useCallback(async () => {
        if (!enabled) return;
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE}/notifications`, {
                params: { limit: 50 },
                withCredentials: true,
            });
            const { notifications: data = [], unreadCount = 0 } = response.data || {};
            const list = Array.isArray(data) ? data : [];

            if (onNewNotification && seenIdsRef.current.size > 0) {
                list.forEach((n) => {
                    if (!seenIdsRef.current.has(n._id) && !n.isRead) {
                        onNewNotification(n);
                    }
                });
            }

            setNotifications(list);
            setUnreadCount(Number(unreadCount) || 0);
            list.forEach(n => seenIdsRef.current.add(n._id));
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setLoading(false);
        }
    }, [enabled, onNewNotification]);

    const markRead = useCallback(async (id) => {
        try {
            await axios.post(`${API_BASE}/notifications/${id}/read`, {}, { withCredentials: true });
            setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Failed to mark notification read:', error);
        }
    }, []);

    const markAllRead = useCallback(async () => {
        try {
            await axios.post(`${API_BASE}/notifications/read-all`, {}, { withCredentials: true });
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all notifications read:', error);
        }
    }, []);

    const deleteNotification = useCallback(async (id) => {
        try {
            await axios.delete(`${API_BASE}/notifications/${id}`, { withCredentials: true });
            setNotifications(prev => prev.filter(n => n._id !== id));
            setUnreadCount(prev => {
                const deleted = notifications.find(n => n._id === id);
                return deleted && !deleted.isRead ? Math.max(0, prev - 1) : prev;
            });
        } catch (error) {
            console.error('Failed to delete notification:', error);
        }
    }, [notifications]);

    useEffect(() => {
        fetchNotifications();
        if (!pollMs || pollMs <= 0) return;
        const interval = setInterval(fetchNotifications, pollMs);
        return () => clearInterval(interval);
    }, [fetchNotifications, pollMs]);

    return {
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        markRead,
        markAllRead,
        deleteNotification,
    };
};

export default useNotifications;
